import { captureException } from '@sentry/core';
import TagManager from 'react-gtm-module';
import type { FilterState } from './filterSlice';
import { getFilterQuery } from './filterHelpers';
import { getGoogleEventForAnalysis, GoogleEvents } from './googleEvents';
import { getHeaders, withForwardedParams } from './utils';
import type {
  SearchResults,
  GroupAnalysisFilter,
  GroupAnalysisType,
  AggregateResponseSingleSearchResultsSearchSummary,
} from '@/generated/types/navigator/types.gen';

export type SuggestionTypes =
  | 'Spans'
  | 'Poles'
  | 'Addresses'
  | 'Coordinates'
  | 'SpansInternal';

interface SuggestionBase {
  value: string;
  subtitle?: string;
}

export type Suggestion =
  | (SuggestionBase & { kind: 'link'; href: string })
  | (SuggestionBase & { kind: 'callback'; onSelect: () => Promise<void> });

export type SuggestionsByType = Partial<Record<SuggestionTypes, Suggestion[]>>;

export const getSuggestions = async (
  searchTerm: string,
  tokenId: string,
  apiIdentity: string,
  filterQuery?: FilterState,
  selectedAnalysis?: GroupAnalysisType,
  maxResults: number = 4
): Promise<SuggestionsByType> => {
  const hasFilter = filterQuery?.isSelected;

  try {
    const url = new URL('/v1/search/', window.location.origin);
    const searchParams = new URLSearchParams();
    searchParams.set('q', searchTerm);
    searchParams.set('limit', maxResults.toString());

    url.search = searchParams.toString();

    const response = await fetch(withForwardedParams(url.toString()), {
      headers: getHeaders(tokenId, apiIdentity, {
        'Content-Type': 'application/json',
      }),
      method: hasFilter ? 'POST' : 'GET',
      body:
        hasFilter && filterQuery
          ? JSON.stringify(
              getFilterQuery(filterQuery) satisfies GroupAnalysisFilter
            )
          : null,
    });

    if (response.status === 401) {
      location.assign('/session');
    }

    const data: AggregateResponseSingleSearchResultsSearchSummary =
      await response.json();

    if (selectedAnalysis) {
      setTimeout(() => {
        TagManager.dataLayer({
          dataLayer: {
            event: getGoogleEventForAnalysis(
              selectedAnalysis,
              GoogleEvents.search
            ),
          },
        });
      }, 500);
    }

    const results = data.data;
    if (
      results &&
      Object.values(results).some(
        (resultsArray) => resultsArray != null && resultsArray.length > 0
      )
    ) {
      return processSearchResults(results, maxResults);
    }
    return {};
  } catch (e) {
    captureException(e);
    return {};
  }
};

const processSearchResults = (
  searchResults: SearchResults,
  maxResultsByType: number
): SuggestionsByType => {
  if (!searchResults) {
    return {
      Spans: [],
      Poles: [],
      SpansInternal: [],
    };
  }

  const spanResults: Suggestion[] =
    searchResults.display_name?.map((r) => ({
      kind: 'link',
      value: r.label,
      href: `/level3/${encodeURIComponent(r.id)}`,
    })) ?? [];

  const internalSpanResults: Suggestion[] =
    searchResults.level3_internal?.map((r) => ({
      kind: 'link',
      value: r.label,
      href: `/level3/${encodeURIComponent(r.id)}`,
    })) ?? [];

  const poleResults: Suggestion[] =
    searchResults.point?.map((r) => ({
      kind: 'link',
      value: r.label,
      href: `/point/${encodeURIComponent(r.id)}`,
    })) ?? [];

  return {
    SpansInternal: internalSpanResults.slice(0, maxResultsByType),
    Spans: spanResults.slice(0, maxResultsByType),
    Poles: poleResults.slice(0, maxResultsByType),
  };
};
